import React, { useState } from 'react';
import { Box, Button, Avatar, Typography, Paper, CircularProgress } from '@mui/material';
import { UploadFile as UploadFileIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';

const AvatarUpload = ({ currentAvatar, onAvatarUpdate, isMobile }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // 处理文件选择
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // 检查文件类型和大小
      if (!selectedFile.type.match('image.*')) {
        setUploadError('请选择有效的图片文件');
        return;
      }
      
      if (selectedFile.size > 2 * 1024 * 1024) { // 2MB限制
        setUploadError('图片大小不能超过2MB');
        return;
      }

      setFile(selectedFile);
      setUploadError('');
      
      // 生成预览
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  // 处理头像上传
  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      // 获取当前用户的邮箱（从本地存储或上下文中获取）
      const userEmail = localStorage.getItem('userEmail') || 'test@example.com';
      formData.append('email', userEmail);
      
      // 这里需要根据实际API调整
      const response = await fetch('/api/upload-avatar', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('上传失败');
      }
      
      const result = await response.json();
      
      if (result.success) {
        // 头像上传成功，调用回调函数更新头像
        if (onAvatarUpdate) {
          onAvatarUpdate(result.data.avatarUrl);
        }
        setFile(null);
        setPreview(null);
      } else {
        setUploadError(result.message || '上传失败');
      }
    } catch (error) {
      console.error('头像上传失败:', error);
      setUploadError('头像上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  // 取消上传
  const handleCancel = () => {
    setFile(null);
    setPreview(null);
    setUploadError('');
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography 
        variant={isMobile ? 'h6' : 'h5'} 
        component="h3" 
        sx={{ mb: 2, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
      >
        头像设置
      </Typography>
      
      <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {/* 当前头像预览 */}
          <Avatar
            src={preview || currentAvatar}
            sx={{
              width: isMobile ? 100 : 150,
              height: isMobile ? 100 : 150,
              mb: 2,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: isMobile ? 50 : 75 }} />
          </Avatar>
          
          {/* 文件选择按钮 */}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="avatar-upload"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="avatar-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              选择图片
            </Button>
          </label>
          
          {/* 上传和取消按钮 */}
          {file && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                {uploading ? '上传中...' : '上传头像'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={uploading}
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                取消
              </Button>
            </Box>
          )}
          
          {/* 错误信息 */}
          {uploadError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {uploadError}
            </Typography>
          )}
          
          {/* 提示信息 */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            支持 JPG、PNG、GIF 格式，文件大小不超过 5MB
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AvatarUpload;